'use client';

import { fetchProductList } from '@/api/product';
import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol, priceInfo as getPriceInfo } from '@/utils/price';
import { useTranslations } from 'next-intl';
import { FC, memo, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useFlashSaleTracking } from '../hooks/useFlashSaleTracking';

// 曝光检测组件
const ExposureDetector: FC<{
  item: any;
  index: number;
  onExposure: (item: any, index: number) => void;
}> = memo(({ item, index, onExposure }) => {
  const [ref, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true, // 使用 triggerOnce: true，这是正确的做法
  });

  useEffect(() => {
    if (inView && item.id) {
      onExposure(item, index);
    }
  }, [inView]);

  return <div ref={ref} className="absolute inset-0 pointer-events-none" />;
});

ExposureDetector.displayName = 'ExposureDetector';

interface PromotionProps {
  promotionData: {
    title_image?: string;
    title_image_alt?: string;
    cart_image?: string;
  };
  promotionList: any[];
  restrictPurchase: number;
  deviceType: EPlatform;
  groupId: string;
  limit: number;
}

// 商品卡片组件 - 移到组件外部并使用memo优化
const ProductItem = memo(
  ({
    item,
    index,
    promotionData,
    isWap,
    currency,
    userInfo,
    onProductClick,
  }: {
    item: any;
    index: number;
    promotionData: any;
    isWap: boolean;
    currency: any;
    userInfo: any;
    onProductClick: (item: any, index: number) => void;
  }) => {
    const priceData = getPriceInfo(userInfo, item, currency);
    const isSoldOut = item.stock <= 0;
    const t = useTranslations('product');

    return (
      <div className="bg-white rounded-[8px] lg:rounded-[10px] 2xl:rounded-[12px] relative">
        <a
          href={`/goods-detail/${item.shop_sku}`}
          className={`w-full h-full box-border flex flex-col justify-between ${
            isWap
              ? 'pt-[36px] px-[12px] pb-[12px]'
              : 'pt-[50px] px-[24px] lg:px-[28px] xl:px-[30px] 2xl:px-[32px] pb-[24px] lg:pb-[28px]'
          }`}
          onClick={(e) => {
            e.preventDefault();
            onProductClick(item, index);
          }}
        >
          {/* 商品图片 */}
          <div className="_product-image flex-1 flex justify-center items-center">
            <Image
              src={item?.image?.product?.[0] || ''}
              alt={item.name}
              width={150}
              height={150}
              className={`w-full h-auto box-border m-auto ${
                isWap
                  ? 'px-[15px] pb-[26px]'
                  : 'px-[14px] lg:px-[20px] pb-[14px] mt-[40px] mb-[30px]'
              }`}
            />
          </div>

          <div className="_product-info">
            {/* 商品名称 */}
            <span
              className={`block mb-[3px] lg:mb-[4px] 2xl:mb-[5px] text-[#808080] ${
                isWap ? 'text-[12px]' : 'text-[16px] lg:text-[18px] xl:text-[20px]'
              }`}
            >
              {item.short_name}
            </span>

            {/* 价格区域 */}
            <div className="flex justify-between items-end">
              {/* 价格文字 */}
              <p className="flex items-center flex-wrap flex-1">
                <span
                  className={`text-[#282828] font-bb notranslate mr-[6px] lg:mr-[8px] ${
                    isWap
                      ? 'text-[16px]'
                      : 'text-[20px] lg:text-[22px] xl:text-[24px] leading-[22px] lg:leading-[26px] xl:leading-[28px]'
                  }`}
                >
                  {formatPriceNumberWithSymbol(priceData.sell_price, currency)}
                </span>
                {priceData.price !== priceData.sell_price && (
                  <span
                    className={`text-[#A9A9A9] line-through notranslate ${
                      isWap ? 'text-[12px]' : 'text-[14px] lg:text-[15px] xl:text-[16px]'
                    }`}
                  >
                    {formatPriceNumberWithSymbol(priceData.price, currency)}
                  </span>
                )}
              </p>

              {/* 购物车图标 */}
              {promotionData.cart_image && (
                <div
                  className={`bg-[#282828] rounded-[5px] lg:rounded-[6px] box-border ${
                    isWap
                      ? 'w-[23px] h-[23px] p-[5px]'
                      : 'w-[28px] lg:w-[30px] xl:w-[32px] h-[28px] lg:h-[30px] xl:h-[32px] p-[5px] lg:p-[6px]'
                  }`}
                >
                  <Image
                    src={promotionData.cart_image}
                    alt=""
                    width={32}
                    height={32}
                    className="w-full h-full"
                  />
                </div>
              )}
            </div>
          </div>

          {/* 售罄遮罩 */}
          {isSoldOut && (
            <div className="absolute inset-0 bg-white/40 rounded-[8px] lg:rounded-[10px] 2xl:rounded-[12px] cursor-default">
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-[27%] aspect-square rounded-full bg-black/60 text-center">
                <div
                  className={`text-white whitespace-pre-wrap ${
                    isWap ? 'text-[12px]' : 'text-[14px] lg:text-[16px] xl:text-[17px]'
                  }`}
                  dangerouslySetInnerHTML={{ __html: t('soldOut.soldOut') }}
                />
              </span>
            </div>
          )}
        </a>
      </div>
    );
  },
);

ProductItem.displayName = 'ProductItem';

export default function Promotion({
  promotionData,
  promotionList,
  restrictPurchase,
  deviceType,
  groupId,
  limit,
}: PromotionProps) {
  const { navigate } = useAppNavigation();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const [moreList, setMoreList] = useState<any[]>([]);
  const [shouldShowViewMoreButton, setShouldShowViewMoreButton] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const isWap = deviceType !== EPlatform.WEB;
  const t = useTranslations('activity.flashSale');

  // 使用埋点追踪hook
  const { trackPromotionProductClick, trackPromotionProductExp } = useFlashSaleTracking();

  // 处理商品点击
  const handleProductClick = (item: any, index: number) => {
    trackPromotionProductClick(item, index);
    navigate(`/goods-detail/${item.shop_sku}`);
  };

  const getMorePromotionData = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      // 使用page参数，从第2页开始（第1页是初始的32个商品）
      const page = currentPage + 1;

      const res = await fetchProductList({
        limit,
        page,
        currency: currency.name,
        group_id: Number(groupId),
      });

      const newList = (res as any).data.list || [];

      if (newList.length === 0) {
        // 没有更多数据，隐藏VIEW MORE按钮
        setShouldShowViewMoreButton(false);
      } else {
        // 追加新数据到现有列表
        setMoreList((prevList) => [...prevList, ...newList]);
        setCurrentPage((prev) => prev + 1);

        // 如果返回的数据少于请求的数量，说明没有更多数据了
        if (newList.length < limit) {
          setShouldShowViewMoreButton(false);
        }
      }
    } catch (error) {
      // 使用console.error是合理的，因为这是错误处理
      // eslint-disable-next-line no-console
      console.error('获取数据失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!promotionList || promotionList.length === 0) {
    return null;
  }

  return (
    <div
      className={`w-full h-full ${isWap ? 'mb-[32px]' : 'mb-[40px] lg:mb-[48px] xl:mb-[54px] 2xl:mb-[56px]'}`}
    >
      {/* 标题图片 - 只在restrictPurchase === 2时显示 */}
      {restrictPurchase === 2 && promotionData.title_image && (
        <Image
          src={promotionData.title_image}
          alt={promotionData.title_image_alt}
          width={1920}
          height={126}
          className="w-full h-full"
        />
      )}
      {/* 促销商品网格 */}
      <div
        className={`grid ${
          isWap
            ? 'mx-[8px] grid-cols-2 gap-[4px]'
            : 'mx-[80px] lg:mx-[120px] xl:mx-[180px] 2xl:mx-[220px] grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-[10px] lg:gap-[12px]'
        }`}
      >
        {promotionList.map((item, index) => (
          <div key={item.id} className="relative">
            <ProductItem
              item={item}
              index={index}
              promotionData={promotionData}
              isWap={isWap}
              currency={currency}
              userInfo={userInfo}
              onProductClick={handleProductClick}
            />
            {/* 曝光检测组件 */}
            <ExposureDetector item={item} index={index} onExposure={trackPromotionProductExp} />
          </div>
        ))}
        {moreList.map((item, index) => (
          <div key={`${item.id}123`} className="relative">
            <ProductItem
              item={item}
              index={index + promotionList.length}
              promotionData={promotionData}
              isWap={isWap}
              currency={currency}
              userInfo={userInfo}
              onProductClick={handleProductClick}
            />
            {/* 曝光检测组件 */}
            <ExposureDetector
              item={item}
              index={index + promotionList.length}
              onExposure={trackPromotionProductExp}
            />
          </div>
        ))}
      </div>
      {/* VIEW MORE按钮 */}
      {shouldShowViewMoreButton && promotionList.length > limit - 1 && (
        <div className="flex justify-center mt-[16px] lg:mt-[32px]">
          <div
            className={`view-more flex items-center justify-center font-bb text-[#282828] transition-all duration-200 no-underline bg-transparent ${
              isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:opacity-90'
            } ${
              !isWap
                ? 'w-[338px] h-[48px] rounded-[1.81vw] border border-[#808080] text-[16px]'
                : 'w-[282px] h-[43px] rounded-[50px] border border-[#808080] text-[14px]'
            }`}
            style={{
              lineHeight: !isWap ? '1.32vw' : '17px',
            }}
            aria-label={t('viewMore')}
            onClick={isLoading ? undefined : getMorePromotionData}
          >
            {isLoading ? t('loading') : t('viewMore')}
          </div>
        </div>
      )}
    </div>
  );
}
